import { Graph, Node, useGraphEvent, useGraphInstance } from "@antv/xflow"
import { useEffect } from "react"
import { eventToObserverable, getNodePortPosition, getNodesNearCoordinate, isNotStream, isPoint, isTemplateLine, setLinePortsVisibility, setNodesPortVisibility } from "./helper"
import { Observable, Subscription, filter, map, merge, switchMap, tap, throttleTime } from "rxjs"
import isNumber from 'lodash/isNumber'
import { Edge, EdgeView, Point, PointLike } from "@antv/x6"
import { EdgeSides, LineType } from "../constant"
import { useEditorEventBusInstance } from "./useEventBusInstance"
import { Equipment } from "@/services/project/equipment/equipment.model"
import { getTagIdByNodeId } from "../nodes"
import { NodeShapes, UnitTagState } from "../services/modal"
import { createNozzleToNodeAndConnect } from "../utils"
import { getLineEndPoint, removeEdgeNozzleFromNode } from "../services/NozzleEdgeLayout"
import { useHasPermissions } from "@/components/OPermission/OPermission"
import { ROLE_DRAFTER } from "@/components/OPermission/PlanPermission"

export const PortConfig = {
  LineColor: '#31d0c6',
  ActiveLineColor: '#0170fe',
  CircleRadius: 5
}

export const Arrowhead = {
  tagName: 'circle',
  attrs: {
    r: PortConfig.CircleRadius,
    fill: '#fff',
    stroke: PortConfig.ActiveLineColor,
    'stroke-width': 2,
    cursor: 'move'
  }
}

function getEndNode(graph: Graph, end: any) {
  if (end.cell && end.port) {
    return graph.getCellById(end.cell)
  }
  return null
}

export const useEdgeEvents = (drawingStream: boolean, isTemplate: boolean) => {
  const graph = useGraphInstance();
  const eventBus = useEditorEventBusInstance()
  const hasPermission = useHasPermissions()

  useGraphEvent('edge:selected', ({ edge }) => {
    if (!hasPermission(ROLE_DRAFTER)) {
      return;
    }
    if (drawingStream) return
    if (edge.getData().type === LineType.Template && !isTemplate) return
    const source = edge.getSource();
    const target = edge.getTarget();
    const sourceNode = getEndNode(graph!, source)
    const targetNode = getEndNode(graph!, target)
    const endTools: any = [
      !(sourceNode && sourceNode.shape === NodeShapes.LineJump) && {
        name: 'source-arrowhead',
        args: Arrowhead
      },
      !(targetNode && targetNode.shape === NodeShapes.LineJump) && {
        name: 'target-arrowhead',
        args: Arrowhead
      }
    ].filter(item => !!item)
    edge.addTools([
      'segments',
      {
        name: 'vertices',
        args: {
          addable: true,
          removable: false,
          stopPropagation: false
        }
      },
      ...endTools,
    ])
  })

  useGraphEvent('edge:changed', ({ edge }) => {
    if (isNotStream(edge)) return;
    const edgeView = <EdgeView>edge.findView(graph!)
    const center = edgeView?.getPointAtRatio(0.5)
    if (!center) return
    const tagNode = graph?.getCellById(getTagIdByNodeId(edge.id)) as Node
      if (tagNode) {
        const tagData = tagNode.getData() as UnitTagState
        tagNode.setPosition({
          x: center!.x + tagData.offset.x,
          y: center!.y + tagData.offset.y,
        })
      }
  })

  useGraphEvent('edge:unselected', ({ edge }) => {
    edge.removeTools()
  })

  useGraphEvent('edge:removed', ({ edge }) => {
    const source: any = edge.getSource()
    const target: any = edge.getTarget()

    function removeNozzle(cellId: string, portId: string) {
      const node = graph!.getCellById(cellId) as Node
      const port = node?.getPort(portId)
      if (port && port.data?.nozzleEdge === true && port?.data?.nozzleEdgeName) {
        removeEdgeNozzleFromNode(node, port, graph!)
      }
      if (node) {
        eventBus.trigger('node:nozzle:changed', {node, type: 'remove'})
      }
    }

    if (source.cell && source.port) {
      removeNozzle(source.cell, source.port)
    }
    if (target.cell && target.port) {
      removeNozzle(target.cell, target.port)
    }
  })

  useEffect(() => {
    if(!graph) return;
    const edgeConnectSub = eventToObserverable(graph, 'edge:connected').pipe(
      filter((evt:any) => {
        return !drawingStream
      }),
      filter(({previousCell, currentCell}) => {
        return previousCell || currentCell
      }),
    ).subscribe(({edge, previousCell, currentCell}) => {
      if (previousCell && previousCell.shape === NodeShapes.SvgStream) {
        const edgeData = edge.getData()
        delete edgeData.streamId;
        edge.setData({...edgeData})
      }
      if (currentCell && currentCell.shape === NodeShapes.SvgStream) {
        const edgeData = edge.getData()
        const cellData = currentCell.getData()
        edge.setData({...edgeData, streamId: cellData.streamId})
      }
    })

    return () => {
      edgeConnectSub.unsubscribe()
    }
  }, [graph, drawingStream])

  useEffect(() => {
    if(!graph) return;
    const edgeConnectSub = eventToObserverable(graph, 'edge:connected').pipe(
      filter((evt:any) => {
        return !drawingStream
      }),
      filter(({previousCell, currentCell}) => {
        return previousCell && !currentCell
      }),
      filter(({previousCell}) => {
        return graph && graph.isNode(previousCell)
      })
    ).subscribe(({edge, previousCell, currentCell, previousPort} ) => {
      const port = previousCell.getPort(previousPort)
      if (port && port.data?.nozzleEdge === true && port?.data?.nozzleEdgeName) {
        removeEdgeNozzleFromNode(previousCell, port, graph!)
      }
    })

    return () => {
      edgeConnectSub.unsubscribe()
    }
  }, [graph, drawingStream])

  useEffect(() => {
    if(!graph) return;
    if (isTemplate) return;
    const sourceChange$ = eventToObserverable(graph, 'edge:change:source').pipe(filter((evt:any) => {
      return !drawingStream
    }));
    const targetChange$ = eventToObserverable(graph, 'edge:change:target').pipe(filter((evt:any) => {
      return !drawingStream
    }));
    const targetChangeSub = edgeSourceTargetChagne(eventToObserverable(graph, 'edge:change:target'), graph, EdgeSides.Target)
    const sourceChangeSub = edgeSourceTargetChagne(eventToObserverable(graph, 'edge:change:source'), graph, EdgeSides.Source)

    const nozzleEdgeMouseUp$ = eventBus.on$('node:nozzleEdge:mouseup')
    const edgeEndsChange$ = merge(sourceChange$, targetChange$);
    const lineChangeSub = lineEndChange(edgeEndsChange$.pipe(filter(({edge}) => {
      const data = edge.getData();
      return data.type === LineType.Line
    })), graph)
    const connecting$ = edgeEndsChange$.pipe(filter(({edge}) => {
      const data = edge.getData();
      return data.type === LineType.FlowLine
    }))
    const autoCreatePortSub = connecting$.pipe(
      switchMap((edgeArgs) => {
        return nozzleEdgeMouseUp$.pipe(map((evt) => ({
          nozzleEvt: evt,
          edgeEvt: edgeArgs
        })))
      })
    ).subscribe((mergedEvt) => {
      const {nozzleEvt, edgeEvt} = mergedEvt as any;
      const node: Node = nozzleEvt.node
      const nozzleEdge: Equipment.NozzleEdge = nozzleEvt.nozzleEdge;

      createNozzleToNodeAndConnect(graph, nozzleEdge, node, eventBus, edgeEvt.edge, edgeEvt.options.propertyPath)
    })
    const lineAlignSub = edgeEndsChange$.pipe(
      switchMap((edgeArgs) => {
        return eventToObserverable(graph, 'edge:mouseup').pipe(
          filter((event: any) => {
            return event.edge.id === edgeArgs.edge.id
          }),
          map(() => edgeArgs)
        )
      })
    ).subscribe((edgeAlignArgs: any) => {
      const { current, edge, options } = edgeAlignArgs
      if (isPoint(current) && options.propertyPath !== undefined) {
        const endType = options.propertyPath === 'target' ? EdgeSides.Target : EdgeSides.Source
        alignWithNearPoint(current, endType, edge, graph)
      }
    })
    

    return () => {
      targetChangeSub.unsubscribe()
      sourceChangeSub.unsubscribe()
      autoCreatePortSub.unsubscribe()
      lineAlignSub.unsubscribe()
      lineChangeSub.unsubscribe()
    }
  }, [graph, drawingStream, isTemplate])

}

function lineEndChange(endChange$: Observable<any>, graph: Graph) {
  let prevNodes: Node[] = []
  return endChange$.subscribe((eventArgs) => {
    const { current } = eventArgs
    if (!(isNumber(current.x) && isNumber(current.y))) {
      if (prevNodes.length > 0) {
        setNodesLinePortsVisibility(graph, prevNodes, 'hidden')
      }
      return []
    }
    const nodes = getNodesNearCoordinate(graph, current, 20)
      if (prevNodes.length > 0) {
      setNodesLinePortsVisibility(graph, prevNodes, 'hidden')
    }
    setNodesLinePortsVisibility(graph, nodes, 'visible')
    prevNodes = nodes
  })
}


function setNodesLinePortsVisibility(graph: Graph, nodes: Node[], visibility: 'hidden' | 'visible') {
  nodes.forEach(item => {
    setLinePortsVisibility(graph, item, visibility)
  })
}

function showPortsWhenConnecting(graph: Graph, current, prevNodes: Node[]) {
  if (!(isNumber(current.x) && isNumber(current.y))) {
    if (prevNodes.length > 0) {
      setNodesPortVisibility(graph, prevNodes, 'hidden')
    }
    return []
  }
  const nodes = getNodesNearCoordinate(graph, current, 20)
  if (prevNodes.length > 0) {
    setNodesPortVisibility(graph, prevNodes, 'hidden')
  }
  setNodesPortVisibility(graph, nodes, 'visible')
  prevNodes = nodes
  return nodes
}

function edgeSourceTargetChagne(stream: Observable<any>, graph: Graph, type: EdgeSides): Subscription {
  let prevNodes: Node[] = []
  return stream.pipe(
    filter(({edge}) => !isNotStream(edge)),
    throttleTime(50),
  ).subscribe({
    next: (eventArgs) => {
      const { current, edge, previous } = eventArgs
      prevNodes = showPortsWhenConnecting(graph, current, prevNodes)
      if (!current.cell) return
      const cell = graph.getCellById(current.cell)
      if (graph.isEdge(cell)) {
        const seter = type === EdgeSides.Source ? edge.setSource : edge.setTarget
        const edgeView = <EdgeView>cell.findView(graph)
        if (edgeView) {
          const length = edgeView.getClosestPointLength(previous)
          seter.apply(edge, [cell, { anchor: { name: 'length', args: { length } } }])
        }
      }
    },
    complete: () => {
      setNodesPortVisibility(graph, prevNodes, 'hidden')
    }
  })
}

function alignWithNearPoint(point: Point, endType: EdgeSides, edge: Edge, graph: Graph) {
  const points = edge.getVertices()
  let nearPoint
  if (points.length === 0) {
    const nearSide: any = endType === EdgeSides.Source ? edge.getTarget() : edge.getSource()
    if (nearSide.cell && nearSide.port) {
      const node = graph.getCellById(nearSide.cell)!
      nearPoint = getNodePortPosition(node as Node, nearSide.port)
    }
  } else {
    nearPoint = endType === EdgeSides.Source ? points[0] : points[points.length - 1]
  }
  if (!nearPoint) return;
  const newPoint = {...point}
  if (Math.abs(point.x - nearPoint.x) < 10) {
    newPoint.x = nearPoint.x
  }
  if (Math.abs(point.y - nearPoint.y) < 10) {
    newPoint.y = nearPoint.y
  }
  if (endType === EdgeSides.Source) {
    edge.setSource(newPoint)
  } else {
    edge.setTarget(newPoint)
  }
  const vertices = edge.getVertices()
  const allPoints = [getLineEndPoint(graph, edge, true), ...vertices, getLineEndPoint(graph, edge, false)]
  const simplifiedPoints = simplifyPolyline(allPoints)
  const newVertices = simplifiedPoints.slice(1, -1)
  edge.setVertices(newVertices)
}

function simplifyPolyline(points: PointLike[]): PointLike[] {
  if (points.length <= 2) return points;
  
  const result: PointLike[] = [points[0]];
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    
    // 判断三点是否共线
    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    
    // 使用叉积判断三点是否共线
    // 如果叉积为0，则三点共线
    const crossProduct = dx1 * dy2 - dx2 * dy1;
    
    if (Math.abs(crossProduct) > 1e-10) { // 使用小数值作为误差范围
      result.push(curr);
    }
  }
  
  result.push(points[points.length - 1]);
  return result;
}